import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import ConfSection_law from "./ConfSection_law";
import ConfSection_hospital from "./ConfSection_hospital";
import ConfSection_default from "./ConfSection_default";
import LoadingSpiner from "@components/ui/LoadingSpiner.tsx";
import { useGroupSelection } from "@hooks/useGroupSelection.ts";
import { toast } from "@/components/toast/CustomToast";
import type { RosterConfigObject } from "@/hooks/useRosterConfig";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import CustomModal from "@/components/modal/CustomModal";

interface ConfTab2Props {
  isAdmin: boolean;
  selectedGroupId: string | null;
  serverConfig: RosterConfigObject | undefined;
  isConfigLoading: boolean;
}

function ConfTab0({
  isAdmin,
  // selectedGroupId,
  serverConfig,
  isConfigLoading,
}: ConfTab2Props) {
  const queryClient = useQueryClient();
  const { groups, selectedGroupId } = useGroupSelection();
  const targetGroupId = isAdmin ? selectedGroupId || undefined : undefined;

  // 로컬 편집 상태
  const [currentConfigures, setCurrentConfigures] =
    useState<RosterConfigObject | null>(null);
  // 서버 refetch로 로컬 편집 상태가 덮어써지는 현상 방지
  const isDirtyRef = useRef(false);

  // 서버 데이터가 로드되면 로컬 상태 동기화
  useEffect(() => {
    if (serverConfig) {
      // 로컬 편집 중이면 서버 데이터로 덮어쓰지 않음 (백그라운드 refetch 방지)
      if (!isDirtyRef.current) {
        setCurrentConfigures(serverConfig);
      }
    }
  }, [serverConfig]);

  // 저장 확인 모달 (저장할까요?)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  // Setter Wrapper
  const setCurrentConfiguresSafe: Dispatch<
    SetStateAction<RosterConfigObject>
  > = (update) => {
    setCurrentConfigures((prev) => {
      const base = prev ?? ({} as RosterConfigObject);
      // 사용자 편집이 시작되면 dirty로 표시하여 refetch 덮어쓰기 방지
      isDirtyRef.current = true;
      return typeof update === "function"
        ? (update as (p: RosterConfigObject) => RosterConfigObject)(base)
        : update;
    });
  };

  // 4. 저장 Mutation
  const saveMutation = useMutation({
    mutationFn: async (newConfig: RosterConfigObject) => {
      const base = `${import.meta.env.VITE_API_BASE_URL}/roster/config/save`;
      const url = targetGroupId
        ? `${base}?group_id=${encodeURIComponent(targetGroupId)}`
        : base;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newConfig),
      });

      if (!response.ok) {
        throw new Error("저장에 실패했습니다.");
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.success("설정이 저장되었습니다.");
      // 저장 완료 후에는 서버 데이터 동기화 허용
      isDirtyRef.current = false;
      // 데이터 갱신 (버전 목록 & 현재 설정)
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configVersions(targetGroupId ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configData(targetGroupId ?? ""),
      });
      // 간호사 데이터 갱신 (Roster_create.tsx와 동일한 로직 사용)
      // useNurses: const groupId = isAdmin ? selectedGroupId : "";
      // Roster_create.tsx: const groupIdForQuery = isAdmin ? (selectedGroupId ?? "") : "";
      // null을 ""로 변환하여 일치시킴
      const groupIdForNurses = isAdmin ? (selectedGroupId ?? "") : "";
      await queryClient.invalidateQueries({
        queryKey: ["NURSES", groupIdForNurses],
      });
      // useFullNurseListQuery의 키도 invalidate
      // useFullNurseListQuery: const queryId = isAdmin ? groupId : undefined;
      // queryKey: ["nurses", "fullSchema", queryId]
      // selectedGroupId가 null이면 undefined로 변환 (useFullNurseListQuery와 일치)
      const queryIdForFullNurses = isAdmin
        ? (selectedGroupId ?? undefined)
        : undefined;
      await queryClient.invalidateQueries({
        queryKey: ["nurses", "fullSchema", queryIdForFullNurses],
      });
    },
  });

  // 현재병동 필터
  const curWard = groups.filter((item) => {
    return item.group_id === selectedGroupId;
  });

  if (isConfigLoading || !currentConfigures) {
    return (
      <div className="py-50">
        <LoadingSpiner />
      </div>
    );
  }

  return (
    <div className="">
      <div className="flex gap-6">
        {/* 기본 설정 */}
        <ConfSection_default
          show_level={currentConfigures.show_level}
          show_preceptor={currentConfigures.show_preceptor}
          curWardName={curWard[0].group_name}
          setCurrentConfigures={setCurrentConfiguresSafe}
        />

        {/* 법정 기준 설정 */}
        <ConfSection_law
          max_conseq_work={currentConfigures.max_conseq_work}
          banned_day_after_eve={currentConfigures.banned_day_after_eve}
          three_seq_nig={currentConfigures.three_seq_nig}
          two_offs_after_three_nig={currentConfigures.two_offs_after_three_nig}
          two_offs_after_two_nig={currentConfigures.two_offs_after_two_nig}
          max_nig_per_month={currentConfigures.max_nig_per_month}
          setCurrentConfigures={setCurrentConfiguresSafe}
        />

        {/* 부서내규설정 */}
        <ConfSection_hospital
          min_exp_per_shift={currentConfigures.min_exp_per_shift}
          req_exp_nurses={currentConfigures.req_exp_nurses}
          two_offs_per_week={currentConfigures.two_offs_per_week}
          sequential_offs={currentConfigures.sequential_offs}
          even_nights={currentConfigures.even_nights}
          nod_noe={currentConfigures.nod_noe}
          preceptor_gauge={currentConfigures.preceptor_gauge}
          setCurrentConfigures={setCurrentConfiguresSafe}
          off_days={currentConfigures.off_days}
          weekly_off_group={currentConfigures.weekly_off_group}
          not_one_night={currentConfigures.not_one_night}
          off_placement_mode={currentConfigures.off_placement_mode}
          preceptee_on={currentConfigures.preceptee_on}
          preceptee_shift_count={currentConfigures.preceptee_shift_count}
          fixed_wanted_use_yn={currentConfigures.fixed_wanted_use_yn}
          use_mid={currentConfigures.use_mid}
          off_first={currentConfigures.off_first}
          off_swap_enabled={currentConfigures.off_swap_enabled}
        />
      </div>

      <div className="conf_schedule__flexRow ">
        <button
          type="button"
          className="conf_schedule__button conf_schedule__button--save"
          onClick={() => setSaveConfirmOpen(true)}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "저장 중..." : "저장하기"}
        </button>
      </div>

      <CustomModal.Confirm
        isOpen={saveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        onConfirm={() => {
          if (currentConfigures) {
            saveMutation.mutate(currentConfigures, {
              onSuccess: () => setSaveConfirmOpen(false),
            });
          }
        }}
        isPending={saveMutation.isPending}
        pendingMsg="설정 저장 중..."
        title="근무표 설정을 저장하시겠습니까?"
        confirmText="예"
        cancelText="아니오"
      />
    </div>
  );
}

export default ConfTab0;
